import { AxiosResponse } from 'axios'
import { IRegister } from 'models/register.model'
import { TSignUpForm } from 'models/user.model'
import { ICompanyProfileSettingsDataUpdate } from 'models/company.model'
import api from './api'

const register = (data: TSignUpForm): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('haveCyberSecurityInsurance', String(data.haveCyberSecurityInsurance))
  formData.append('alternativeNames', JSON.stringify(data.alternativeNames))
  formData.append('secondaryEmails', JSON.stringify(data.secondaryEmails))
  formData.append('email', data.email)
  formData.append('gender', data.gender)
  formData.append('birthDate', data.birthDate)
  formData.append('address[street]', data.address.street)
  formData.append('address[number]', data.address.number)
  formData.append('address[postalCode]', data.address.postalCode)
  formData.append('address[city]', data.address.city)
  formData.append('address[country]', data.address.country)
  formData.append('address[countryState]', data.address.countryState)
  formData.append('havePasswordManager', String(data.havePasswordManager))
  formData.append('useMFA', String(data.useMFA))
  formData.append('haveResponsePlan', String(data.haveResponsePlan))
  formData.append('top10Apps', JSON.stringify(data.top10Apps))
  formData.append('image', data.image as Blob)

  return api.post('/v1/register', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const updatePhoneNumber = (newPhoneNumber: string, email: string): Promise<AxiosResponse> => {
  return api.patch(`/v1/user/phone`, { phone: newPhoneNumber, email })
}

const verifyEmailStatus = (email: string): Promise<AxiosResponse> => {
  return api.get(`/v1/user/status/${email}`)
}

const updateRegisterField = (field: string, data): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append(field, JSON.stringify(data))
  return api.put('/v1/register', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const updateRegister = (data: TSignUpForm): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('fullname', data.fullname)
  formData.append('alternativeNames', JSON.stringify(data.alternativeNames))
  formData.append('secondaryEmails', JSON.stringify(data.secondaryEmails))
  formData.append('gender', data.gender)
  formData.append('haveCyberSecurityInsurance', String(data.haveCyberSecurityInsurance))
  formData.append('birthDate', data.birthDate)
  formData.append('address[street]', data.address.street)
  formData.append('address[number]', data.address.number)
  formData.append('address[postalCode]', data.address.postalCode)
  formData.append('address[city]', data.address.city)
  formData.append('address[country]', data.address.country)
  formData.append('address[countryState]', data.address.countryState)
  formData.append('havePasswordManager', String(data.havePasswordManager))
  formData.append('useMFA', String(data.useMFA))
  formData.append('haveResponsePlan', String(data.haveResponsePlan))
  formData.append('top10Apps', JSON.stringify(data.top10Apps))
  formData.append('image', data.image as Blob)

  return api.put('/v1/register', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

const getRegister = async (): Promise<IRegister> => {
  const { data } = await api.get('/v1/register')
  return data
}

const getImageBlob = async (): Promise<string | null> => {
  let userAvatar: string | null = null
  const { data } = await api.get(`/v1/register`)
  if (data.image) {
    try {
      const path = data.image.split(data.cognitoId).pop()
      const imgPath = `/v1/register${path}`
      if (path) {
        const imageBlob = await api.get(imgPath, { responseType: 'blob' })
        userAvatar = URL.createObjectURL(imageBlob.data)
      }
    } catch {
      userAvatar = null
    }
  }
  return userAvatar
}

const isAdmin = (email: string): Promise<AxiosResponse> => {
  return api.post('/v1/auth/verify-email', { email })
}

const deleteRegister = (): Promise<AxiosResponse> => {
  return api.delete('/v1/register')
}

const deleteOneRegister = (cognitoId: string): Promise<AxiosResponse> => {
  return api.delete(`/v1/register/${cognitoId}`)
}

export const createInitialRegister = (
  fullname: string,
  email: string,
  phoneNumber: string,
  company: string | null,
): Promise<AxiosResponse> => {
  const newRegister = {
    fullname,
    email,
    phoneNumber,
  }
  if (company) newRegister['company'] = company
  return api.post('/v1/register/before', newRegister)
}

interface ICheckDomainResponse {
  domainExists: boolean
}
const isDomainBeenUsed = async (domain: string): Promise<ICheckDomainResponse> => {
  const { data } = await api.get(`/v1/company/checkDomain/${domain}`)
  return data
}

const updateHaveResponsePlanDateInRegister = async () => {
  return api.put('/v1/register/lastResponsePlanCheck', { date: new Date() })
}

/**
 * Unified sign-up endpoint that handles all registration orchestration on the backend
 * This replaces the old flow of: frontend → Cognito → backend confirm
 * New flow: frontend → backend (handles Cognito internally)
 */
export interface IUnifiedSignUpRequest {
  fullname: string
  email: string
  phoneNumber: string
  password: string
  company?: string
  inviteCode?: string
}

export interface IUnifiedSignUpResponse {
  success: boolean
  cognitoId?: string
  email?: string
  requiresMFA?: boolean
  messageCode?: string
  message?: string
}

const unifiedSignUp = async (data: IUnifiedSignUpRequest): Promise<IUnifiedSignUpResponse> => {
  const { data: response } = await api.post<IUnifiedSignUpResponse>('/v2/auth/register', data)
  return response
}

/**
 * @deprecated Use unifiedSignUp instead. This will be removed after migration is complete.
 */
const confirmSignUp = async (identifier?: string) => {
  await api.post('/v2/user/auth/confirm-account', {
    identifier,
  })
}

const setMfaOptions = async (email: string, mfaOption: 'phone' | 'email') => {
  await api.post('/v2/user/auth/toggle-mfa', {
    email,
    preferred: mfaOption,
  })
}

const getCompanyProfile = async (companyId: string) => {
  const { data } = await api.get(`/v2/company/${companyId}/settings/company-profile`)
  return data
}

const updateCompanyProfile = async (
  companyId: string,
  profileData: ICompanyProfileSettingsDataUpdate,
): Promise<AxiosResponse> => {
  return api.put(`/v2/company/${companyId}/settings/company-profile`, profileData, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

export default {
  getRegister,
  register,
  deleteRegister,
  updateRegister,
  isAdmin,
  isDomainBeenUsed,
  updateRegisterField,
  verifyEmailStatus,
  createInitialRegister,
  updatePhoneNumber,
  updateHaveResponsePlanDateInRegister,
  deleteOneRegister,
  getImageBlob,
  confirmSignUp,
  setMfaOptions,
  getCompanyProfile,
  updateCompanyProfile,
  unifiedSignUp,
}
